import AccessControl from "@/v2/components/rbac/AccessControl";
import InvoiceDeleteButton from "@/v2/components/ui/button/delete/Invoice";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import EditInvoice from "@/v2/page/invoice/Edit";
import { useQuery } from "@tanstack/react-query";
import {
    FileText,
    DollarSign,
    Calendar,
    Link2,
    Building,
    Hash,
    Mail,
    Phone,
    Globe,
    Receipt,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ViewLayout from "@/v2/layout/View";
import { CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { formatDate } from "@/v2/utils";
import FindOneInvoice from "@/v2/api/invoice/FindOne";

const CDN_URL = import.meta.env.VITE_CDN_URL;

const NavButtons = ({ id }: { id: string }) => {
    return (
        <>
            <AccessControl module="INVOICE_MANAGEMENT" requestedAccess={"PUT"}>
                <EditInvoice type="RECEIVABLE_INVOICE" isInside id={id} />
            </AccessControl>
            <InvoiceDeleteButton isInside id={id} />
        </>
    );
};

export default function ViewOneReceivableInvoice() {
    const { id: invoiceId } = useParams();
    const navigate = useNavigate();

    if (!invoiceId) navigate(-1);

    const query = useQuery({
        queryKey: ["invoices", invoiceId],
        queryFn: () => FindOneInvoice(invoiceId!),
    });

    if (query.isLoading || query.isFetching) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    const invoiceData = query.data;

    return (
        <ViewLayout
            title="Receivable Invoice Details"
            description="Information about the receivable invoice"
            icon={FileText}
            buttons={<NavButtons id={invoiceId!} />}
        >
            <CardContent className="space-y-8">
                {/* Invoice Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Invoice Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                Invoice Number
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.invoice_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                PO Number
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.reference_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                Invoice Date
                            </div>
                            <p className="text-base font-medium">
                                {formatDate(invoiceData?.invoice_date)}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                Due Date
                            </div>
                            <p className="text-base font-medium">
                                {formatDate(invoiceData?.invoice_due_date)}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                Time Sheet Month
                            </div>
                            <p className="text-base font-medium">
                                {
                                    invoiceData?.months_and_year_against_time_sheet
                                }
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Financial Details */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Financial Details
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                Invoice Amount
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.invoice_amount?.toFixed(2) ||
                                    "0.00"}{" "}
                                SR
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                VAT Amount
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.invoice_vat?.toFixed(2) || "0.00"}{" "}
                                SR
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                Total Amount (including VAT)
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.invoice_amount_including_vat?.toFixed(
                                    2,
                                ) || "0.00"}{" "}
                                SR
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                Total Received
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.invoice_paid_amount?.toFixed(2) ||
                                    "0.00"}{" "}
                                SR
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                Pending Amount
                            </div>
                            <p className="text-base font-medium">
                                {(
                                    invoiceData?.invoice_amount_including_vat -
                                    (invoiceData?.invoice_paid_amount || 0)
                                ).toFixed(2) || "0.00"}{" "}
                                SR
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Additional Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Additional Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Remarks
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.remarks || "N/A"}
                            </p>
                        </div>

                        {invoiceData?.invoice_receipt && (
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-muted-foreground">
                                    <Receipt className="mr-2 h-4 w-4" />
                                    Invoice Receipt
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Link2 className="h-4 w-4 text-blue-500" />
                                    <a
                                        href={`${CDN_URL}/${invoiceData.invoice_receipt}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-base font-medium text-blue-500 hover:underline"
                                    >
                                        Download Receipt
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Separator />

                {/* Client Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Client Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building className="mr-2 h-4 w-4" />
                                Client Name
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.client?.full_name || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                Short Code
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.client?.short_code || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Mail className="mr-2 h-4 w-4" />
                                Email
                            </div>
                            <p className="break-all text-base font-medium">
                                {invoiceData?.client?.email || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Phone className="mr-2 h-4 w-4" />
                                Phone Number
                            </div>
                            <p className="text-base font-medium">
                                {invoiceData?.client?.phone_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Globe className="mr-2 h-4 w-4" />
                                Website
                            </div>
                            <p className="break-all text-base font-medium">
                                {invoiceData?.client?.website || "N/A"}
                            </p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </ViewLayout>
    );
}
